import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarDays, Droplet, Plus, Loader2, Sparkles, Heart, Egg, Calendar as CalendarIcon } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { format, addDays } from "date-fns";

const CycleCalendar = () => {
  const { token } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingUp, setSettingUp] = useState(false);

  // Form state for daily log
  const [flowIntensity, setFlowIntensity] = useState("");
  const [mood, setMood] = useState("");
  const [symptoms, setSymptoms] = useState([]);
  const [notes, setNotes] = useState("");
  const [isPeriodStart, setIsPeriodStart] = useState(false);
  const [isPeriodEnd, setIsPeriodEnd] = useState(false);

  // Setup form state
  const [lastPeriodDate, setLastPeriodDate] = useState("");
  const [cycleLength, setCycleLength] = useState("28");
  const [periodLength, setPeriodLength] = useState("5");

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const symptomOptions = [
    "Cramps", "Bloating", "Headache", "Fatigue", "Breast tenderness",
    "Mood swings", "Acne", "Back pain", "Nausea", "Food cravings"
  ];

  const moodOptions = [
    "Happy", "Calm", "Energetic", "Anxious", "Irritable",
    "Sad", "Tired", "Focused", "Creative", "Emotional"
  ];

  useEffect(() => {
    fetchLogs();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/cycle/logs`, { headers, withCredentials: true });
      // API returns {logs: [...]} so extract the logs array
      setLogs(res.data.logs || []);
    } catch (error) {
      // data loads with fallback state
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupCycle = async () => {
    if (!lastPeriodDate) {
      toast.error("Please enter your last period start date");
      return;
    }

    setSettingUp(true);
    try {
      const res = await axios.post(`${API}/cycle/setup`, {
        last_period_date: lastPeriodDate,
        cycle_length: parseInt(cycleLength),
        period_length: parseInt(periodLength)
      }, { headers, withCredentials: true });

      toast.success(`Calendar auto-filled! ${res.data.logs_created} days added.`);
      setSetupDialogOpen(false);
      fetchLogs();
      
      // Show predictions
      const predictions = res.data.predictions;
      toast.info(`Next period: ${new Date(predictions.next_period).toLocaleDateString()}`, {
        duration: 5000
      });
    } catch (error) {
      toast.error("Failed to setup cycle");
      // data loads with fallback state
    } finally {
      setSettingUp(false);
    }
  };

  const handleSaveLog = async () => {
    setSaving(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");

      // Save the rich daily log (flow, mood, symptoms, notes) via the legacy endpoint
      // so existing reports / cycle phase calcs stay in sync.
      await axios.post(`${API}/cycle/log`, {
        date: dateStr,
        flow_intensity: flowIntensity || null,
        mood: mood || null,
        symptoms,
        notes: notes || null,
        is_period_start: isPeriodStart,
        is_period_end: isPeriodEnd
      }, { headers, withCredentials: true });

      // Mirror to the new period tracking collection so Cycle Intelligence + AI insights
      // receive structured period entries.
      try {
        if (isPeriodStart) {
          await axios.post(`${API}/period/start`, {
            start_date: dateStr,
            notes: notes || null,
          }, { headers, withCredentials: true });
        } else if (isPeriodEnd) {
          const currentRes = await axios.get(`${API}/period/current`, { headers, withCredentials: true });
          if (currentRes.data?.id) {
            await axios.post(
              `${API}/period/end/${currentRes.data.id}?end_date=${dateStr}`,
              null,
              { headers, withCredentials: true }
            );
          }
        } else if (flowIntensity) {
          // Bleeding day without a start/end flag — belongs to ongoing or recent period.
          const flowLevelMap = { light: 2, medium: 3, heavy: 5 };
          await axios.post(`${API}/period/bleeding-day`, {
            date: dateStr,
            flow_level: flowLevelMap[flowIntensity] || 3,
          }, { headers, withCredentials: true });
        }
      } catch (periodErr) {
        // Non-blocking — legacy log is the source of truth; period mirror is enhancement only.
        console.warn("Period mirror sync skipped:", periodErr?.response?.data?.detail);
      }

      toast.success("Log saved successfully!");

      // First-period-log nudge: set expectations about cycle reminder emails
      if (isPeriodStart && !localStorage.getItem("scf_cycle_reminder_nudge_shown")) {
        setTimeout(() => {
          toast("We'll email you 2 days before your next period ✨", {
            description: "Manage this anytime in Settings → Weekly Cycle Digest.",
            duration: 6000,
          });
        }, 800);
        localStorage.setItem("scf_cycle_reminder_nudge_shown", "1");
      }

      setDialogOpen(false);
      fetchLogs();
      resetForm();
    } catch (error) {
      toast.error("Failed to save log");
      // data loads with fallback state
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFlowIntensity("");
    setMood("");
    setSymptoms([]);
    setNotes("");
    setIsPeriodStart(false);
    setIsPeriodEnd(false);
  };

  const toggleSymptom = (symptom) => {
    setSymptoms(prev =>
      prev.includes(symptom)
        ? prev.filter(s => s !== symptom)
        : [...prev, symptom]
    );
  };

  // Get logged dates for calendar highlighting
  const loggedDates = logs.reduce((acc, log) => {
    acc[log.date] = log;
    return acc;
  }, {});

  const modifiers = {
    period: (date) => {
      const dateStr = format(date, "yyyy-MM-dd");
      const log = loggedDates[dateStr];
      return log?.flow_intensity || log?.is_period_start || log?.is_period_end || log?.cycle_type === "period";
    },
    ovulation: (date) => {
      const dateStr = format(date, "yyyy-MM-dd");
      const log = loggedDates[dateStr];
      return log?.is_ovulation || log?.cycle_type === "ovulation";
    },
    fertile: (date) => {
      const dateStr = format(date, "yyyy-MM-dd");
      const log = loggedDates[dateStr];
      // Only mark as fertile if NOT ovulation day (ovulation has its own highlight)
      const isOvulation = log?.is_ovulation || log?.cycle_type === "ovulation";
      return (log?.is_fertile || log?.cycle_type === "fertile") && !isOvulation;
    },
    logged: (date) => {
      const dateStr = format(date, "yyyy-MM-dd");
      return !!loggedDates[dateStr];
    }
  };

  const modifiersStyles = {
    period: {
      backgroundColor: "rgba(239, 68, 68, 0.5)",
      borderRadius: "50%",
      color: "white"
    },
    ovulation: {
      backgroundColor: "rgba(250, 204, 21, 0.7)",
      borderRadius: "50%",
      color: "black",
      fontWeight: "bold"
    },
    fertile: {
      backgroundColor: "rgba(34, 197, 94, 0.3)",
      borderRadius: "50%"
    },
    logged: {
      border: "2px solid rgba(157, 78, 221, 0.5)"
    }
  };

  const selectedLog = loggedDates[format(selectedDate, "yyyy-MM-dd")];

  // Check if user has any logs
  const hasLogs = logs.length > 0;

  return (
    <div className="space-y-6" data-testid="cycle-calendar-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <CalendarDays className="w-8 h-8 text-cycle-ovulation" />
          Cycle Calendar
        </h1>
        <p className="text-mystic-200/70">Track your menstrual cycle day by day</p>
      </motion.div>

      {/* Legend */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        <Card className="glass">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-6 justify-center">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-red-500/40"></div>
                <span className="text-mystic-200 text-sm">Period</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-yellow-500/50"></div>
                <span className="text-mystic-200 text-sm">Ovulation</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-green-500/30"></div>
                <span className="text-mystic-200 text-sm">Fertile Window</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 border-mystic-500/50"></div>
                <span className="text-mystic-200 text-sm">Logged</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Setup Banner - Show if no logs */}
      {!hasLogs && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass border-mystic-300/30 bg-gradient-to-r from-mystic-600/20 to-pink-600/20">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-mystic-500/20">
                    <Sparkles className="w-8 h-8 text-mystic-300" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">Quick Setup</h3>
                    <p className="text-mystic-200/70">Auto-fill your calendar with period, ovulation & fertile days</p>
                  </div>
                </div>
                <Button
                  onClick={() => setSetupDialogOpen(true)}
                  className="bg-mystic-500 hover:bg-mystic-400 text-white rounded-full px-6"
                  data-testid="quick-setup-btn"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Setup My Cycle
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2"
        >
          <Card className="glass">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Calendar</CardTitle>
              <Button
                onClick={() => setSetupDialogOpen(true)}
                variant="outline"
                size="sm"
                className="border-mystic-500/50 text-mystic-300 hover:bg-mystic-500/20"
                data-testid="setup-cycle-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Auto-Fill
              </Button>
            </CardHeader>
            <CardContent className="p-6">
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
                </div>
              ) : (
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  modifiers={modifiers}
                  modifiersStyles={modifiersStyles}
                  className="rounded-xl bg-mystic-900/50 p-4"
                  classNames={{
                    months: "space-y-4",
                    month: "space-y-4",
                    caption: "flex justify-center pt-1 relative items-center text-white",
                    caption_label: "text-sm font-medium",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse space-y-1",
                    head_row: "flex",
                    head_cell: "text-mystic-400 rounded-md w-9 font-normal text-[0.8rem]",
                    row: "flex w-full mt-2",
                    cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-transparent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                    day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-mystic-700/50 rounded-full text-white",
                    day_selected: "bg-mystic-500 text-white hover:bg-mystic-500 focus:bg-mystic-500",
                    day_today: "bg-mystic-800 text-white",
                    day_outside: "text-mystic-600 opacity-50",
                    day_disabled: "text-mystic-600 opacity-50",
                    day_hidden: "invisible",
                  }}
                />
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Selected Day Info */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                {format(selectedDate, "MMMM d, yyyy")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedLog ? (
                <div className="space-y-3">
                  {selectedLog.cycle_type === "period" || selectedLog.flow_intensity ? (
                    <div className="flex items-center gap-2 text-red-400">
                      <Droplet className="w-4 h-4" />
                      <span>Period Day {selectedLog.flow_intensity && `(${selectedLog.flow_intensity})`}</span>
                    </div>
                  ) : null}
                  
                  {selectedLog.cycle_type === "ovulation" || selectedLog.is_ovulation ? (
                    <div className="flex items-center gap-2 text-yellow-400">
                      <Egg className="w-4 h-4" />
                      <span>Ovulation Day</span>
                    </div>
                  ) : null}
                  
                  {selectedLog.cycle_type === "fertile" || selectedLog.is_fertile ? (
                    <div className="flex items-center gap-2 text-green-400">
                      <Heart className="w-4 h-4" />
                      <span>Fertile Window</span>
                    </div>
                  ) : null}

                  {selectedLog.mood && (
                    <div className="text-mystic-200">
                      <span className="text-mystic-400">Mood:</span> {selectedLog.mood}
                    </div>
                  )}

                  {selectedLog.symptoms?.length > 0 && (
                    <div>
                      <span className="text-mystic-400 text-sm">Symptoms:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedLog.symptoms.map((s, i) => (
                          <span key={`symptom-${i}`} className="text-xs px-2 py-1 glass-light rounded-full text-mystic-200">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedLog.notes && (
                    <div>
                      <span className="text-mystic-400 text-sm">Notes:</span>
                      <p className="text-mystic-200 text-sm mt-1">{selectedLog.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-mystic-400 text-sm">No log for this date</p>
              )}

              {/* Add/Edit Log Button */}
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-mystic-600 hover:bg-mystic-500 text-white rounded-full" data-testid="add-log-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {selectedLog ? "Edit Log" : "Add Log"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="glass border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-white">
                      Log for {format(selectedDate, "MMMM d, yyyy")}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {/* Period Markers */}
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={isPeriodStart}
                          onCheckedChange={setIsPeriodStart}
                          className="border-mystic-400"
                        />
                        <span className="text-mystic-100 text-sm">Period Start</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={isPeriodEnd}
                          onCheckedChange={setIsPeriodEnd}
                          className="border-mystic-400"
                        />
                        <span className="text-mystic-100 text-sm">Period End</span>
                      </label>
                    </div>

                    {/* Flow Intensity */}
                    <div className="space-y-2">
                      <Label className="text-mystic-100">Flow Intensity</Label>
                      <Select value={flowIntensity} onValueChange={setFlowIntensity}>
                        <SelectTrigger className="bg-mystic-900/50 border-white/10 text-white">
                          <SelectValue placeholder="Select intensity" />
                        </SelectTrigger>
                        <SelectContent className="bg-mystic-800 border-white/10">
                          <SelectItem value="light" className="text-white">Light</SelectItem>
                          <SelectItem value="medium" className="text-white">Medium</SelectItem>
                          <SelectItem value="heavy" className="text-white">Heavy</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Mood */}
                    <div className="space-y-2">
                      <Label className="text-mystic-100">Mood</Label>
                      <Select value={mood} onValueChange={setMood}>
                        <SelectTrigger className="bg-mystic-900/50 border-white/10 text-white">
                          <SelectValue placeholder="How are you feeling?" />
                        </SelectTrigger>
                        <SelectContent className="bg-mystic-800 border-white/10">
                          {moodOptions.map((m) => (
                            <SelectItem key={m} value={m.toLowerCase()} className="text-white">
                              {m}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Symptoms */}
                    <div className="space-y-2">
                      <Label className="text-mystic-100">Symptoms</Label>
                      <div className="flex flex-wrap gap-2">
                        {symptomOptions.map((symptom) => (
                          <button
                            key={symptom}
                            type="button"
                            onClick={() => toggleSymptom(symptom)}
                            className={`px-3 py-1 rounded-full text-sm transition-all ${
                              symptoms.includes(symptom)
                                ? "bg-mystic-500 text-white"
                                : "glass-light text-mystic-200 hover:bg-mystic-700"
                            }`}
                          >
                            {symptom}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="space-y-2">
                      <Label className="text-mystic-100">Notes</Label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any additional notes..."
                        className="bg-mystic-900/50 border-white/10 text-white placeholder:text-white/30"
                      />
                    </div>

                    <Button
                      onClick={handleSaveLog}
                      disabled={saving}
                      className="w-full bg-mystic-500 hover:bg-mystic-400 text-white rounded-full"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Save Log
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Setup Cycle Dialog */}
      <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
        <DialogContent className="glass border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-mystic-300" />
              Setup Your Cycle
            </DialogTitle>
            <DialogDescription className="text-mystic-200/70">
              Enter your cycle information to auto-fill the calendar with period days, ovulation, and fertile windows.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Last Period Date */}
            <div className="space-y-2">
              <Label className="text-mystic-100">When did your last period start?</Label>
              <Input
                type="date"
                value={lastPeriodDate}
                onChange={(e) => setLastPeriodDate(e.target.value)}
                className="bg-mystic-900/50 border-white/10 text-white"
                data-testid="last-period-input"
              />
            </div>

            {/* Cycle Length */}
            <div className="space-y-2">
              <Label className="text-mystic-100">Average cycle length (days)</Label>
              <Select value={cycleLength} onValueChange={setCycleLength}>
                <SelectTrigger className="bg-mystic-900/50 border-white/10 text-white" data-testid="cycle-length-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-mystic-800 border-white/10 max-h-60">
                  {Array.from({ length: 20 }, (_, i) => i + 21).map((days) => (
                    <SelectItem key={days} value={days.toString()} className="text-white">
                      {days} days
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-mystic-400">From first day of period to day before next period</p>
            </div>

            {/* Period Length */}
            <div className="space-y-2">
              <Label className="text-mystic-100">How many days do you typically bleed?</Label>
              <Select value={periodLength} onValueChange={setPeriodLength}>
                <SelectTrigger className="bg-mystic-900/50 border-white/10 text-white" data-testid="period-length-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-mystic-800 border-white/10">
                  {Array.from({ length: 10 }, (_, i) => i + 2).map((days) => (
                    <SelectItem key={days} value={days.toString()} className="text-white">
                      {days} days
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Preview */}
            <div className="glass-light rounded-lg p-4 space-y-2">
              <h4 className="text-mystic-300 font-medium text-sm">What will be calculated:</h4>
              <ul className="text-mystic-200/70 text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500/40"></div>
                  Period days (based on bleeding length)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500/50"></div>
                  Ovulation day (~14 days before next period)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500/30"></div>
                  Fertile window (5 days before ovulation)
                </li>
                <li className="flex items-center gap-2">
                  <CalendarIcon className="w-3 h-3 text-mystic-400" />
                  Next 3 cycles prediction
                </li>
              </ul>
            </div>

            <Button
              onClick={handleSetupCycle}
              disabled={settingUp || !lastPeriodDate}
              className="w-full bg-mystic-500 hover:bg-mystic-400 text-white rounded-full py-6"
              data-testid="setup-cycle-submit-btn"
            >
              {settingUp ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Auto-Fill My Calendar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CycleCalendar;
